import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { fetchProposalFormData } from "../OrderService";
import { restoreNestedFiles } from "../../../../Utils/craftFormFiles";

// API file fields come back as { media_id, file_name, media_url, ... } — the
// same shape OrderService.js, PolicyModal.js and ProposalModal.js already
// read elsewhere. @craft-form/renderer's file-upload field instead reads
// `fileName`/`name` and `url`/`signedUrl` (see its README). Detecting by
// shape rather than hardcoding each of FIELDS' file-upload field names
// (dn_pancard, dn_aadhcard, dn_aadhcard_back, dn_mbbs_qual_cert,
// dn_qualification_cert) means a new file field needs no matching change here.
const isApiFileRef = (value) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  typeof value.file_name === "string" &&
  typeof value.media_url === "string";

// Fields whose saved value is a full date ("01-01-2025") but whose select
// only holds/emits a bare year — either a *_YEAR entity dropdown
// (PRACTICING_SINCE_YEAR, QUALIFICATION_YEAR, ...) or (as dn_pracsinc_1 and
// dn_yearofhighqual are configured now) a client-generated yearRange select
// (dataSourceType: "generated", resolveGeneratedOptions in the renderer's
// engine — its options carry a NUMBER `value`, e.g. `{label:"2025",
// value:2025}`, not a string). Confirmed against a real getProposalFormData
// response, where both fields come back as "01-01-2025". Without extraction
// the select silently shows unselected despite having a saved value. Same
// regex ProposalModal.js's formatYear already uses for the old renderer's
// read-only view. Derived from FIELDS instead of hardcoding field names so a
// future *_YEAR/yearRange select is covered for free.
const isGeneratedYearRangeField = (field) =>
  field.dataSourceType === "generated" &&
  field.generatedOptions?.kind === "yearRange";

const getYearSelectFieldNames = (fields) =>
  new Set(
    (Array.isArray(fields) ? fields : [])
      .filter(
        (field) =>
          field.type === "select" &&
          (field.entityType?.endsWith("_YEAR") ||
            isGeneratedYearRangeField(field))
      )
      .map((field) => field.name)
  );

// Numeric, not string: matches resolveGeneratedOptions' `value` type for the
// yearRange fields above, so the select's strict value===option.value check
// (custom-select.tsx) actually finds the option and shows it as selected.
const extractYear = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return value;
  const match = value.match(/(\d{4})/);
  return match ? Number(match[1]) : value;
};

// Inverse of extractYear, for the submit path (useSubmitProposalForm) — the
// API returns and expects these fields as "01-01-<year>", the select field
// only ever holds/emits the bare year (a number, per extractYear above, but
// accepting a numeric string too in case a field ever seeds one directly).
// Anything else (already a date string, "", null) is left untouched rather
// than guessed at.
export const denormalizeProposalRecord = (values, fields) => {
  if (!values) return values;

  const yearSelectFieldNames = getYearSelectFieldNames(fields);

  const restoreYear = (value) => {
    if (typeof value === "number" && Number.isInteger(value)) {
      return `01-01-${value}`;
    }
    if (typeof value === "string" && /^\d{4}$/.test(value)) {
      return `01-01-${value}`;
    }
    return value;
  };

  const denormalized = {};
  for (const [key, value] of Object.entries(values)) {
    denormalized[key] = yearSelectFieldNames.has(key)
      ? restoreYear(value)
      : value;
  }
  return denormalized;
};

const toRendererFileRef = (value) =>
  isApiFileRef(value)
    ? { ...value, name: value.file_name, url: value.media_url }
    : value;

const normalizeProposalRecord = (record, fields) => {
  if (!record) return record;

  const yearSelectFieldNames = getYearSelectFieldNames(fields);

  const normalized = {};
  for (const [key, value] of Object.entries(record)) {
    if (isApiFileRef(value)) {
      normalized[key] = toRendererFileRef(value);
    } else if (yearSelectFieldNames.has(key)) {
      normalized[key] = extractYear(value);
    } else {
      normalized[key] = value;
    }
  }

  // A relation-table row's document is saved under its full path
  // (`<tableKey>.<rowIndex>.<attributeKey>`) and comes back as a top-level key,
  // since the API merges every media row into the form JSON by attribute key.
  // Put it back inside its row, where the table's file cell reads it.
  return restoreNestedFiles(normalized, toRendererFileRef);
};

// `enabled` lets a caller that only needs this conditionally (e.g.
// ProposalModal, which fetches it purely to seed the craft-form renderer's
// react-hook-form instance) hold off firing the request until that
// condition is true, instead of paying for an unused round trip on every
// proposal open.
const useProposalFormData = (
  orderId,
  displayMode,
  organizationId,
  enabled = true,
) => {
  const fields = useSelector((state) => state.order.ProposalFieldsJSON);

  return useQuery({
    queryKey: ["proposalFormData", orderId, displayMode, organizationId],
    queryFn: () => fetchProposalFormData(orderId, displayMode, organizationId),
    enabled: enabled && !!orderId && !!organizationId,
    select: (record) => normalizeProposalRecord(record, fields),
  });
};

export default useProposalFormData;
